import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { DailyOpenHoursSettings, TimeRange } from '@/shared/types/availability';
import { localId } from '@/shared/components/schedule/utils';
import {
  DayOfWeek,
  Maybe,
  StylistCalendar,
  NylasConnection,
} from '@/shared/types/generated';
import { defaultSettings } from '@/shared/hooks/useAvailabilitySetting';
import {
  getNextTimeRange,
  validateTimeRangeOverlap,
} from '@/shared/components/schedule/utils';

interface AvailabilityState {
  stylistId?: Maybe<number>;
  dailyOpenHoursSettings: DailyOpenHoursSettings;
  calendarIds: Maybe<number>[];
  bookingCalendarId?: number;
  timezone?: string;
  calendars: Maybe<StylistCalendar>[];
  nylasConnections: Maybe<NylasConnection>[];

  setDailyOpenHoursSettings: (settings: DailyOpenHoursSettings) => void;
  toggleDay: (day: DayOfWeek) => void;
  addTimeRange: (day: DayOfWeek) => void;
  removeTimeRange: (day: DayOfWeek, rangeId: string) => void;
  handleTimeChange: (
    day: DayOfWeek,
    rangeId: string,
    field: 'startTime' | 'endTime',
    value: string,
  ) => void;

  setCalendarIds: (ids: Maybe<number>[]) => void;
  setBookingCalendarId: (id?: number) => void;
  setTimezone: (timezone?: string) => void;
  setCalendars: (calendars: Maybe<StylistCalendar>[]) => void;
  setNylasConnections: (connections: Maybe<NylasConnection>[]) => void;
  setStylistId: (stylistId: Maybe<number>) => void;
  disconnectCalendar: () => void;
}

const useAvailabilityStore = create<AvailabilityState>()(
  immer((set) => ({
    stylistId: undefined,
    dailyOpenHoursSettings: defaultSettings,
    calendarIds: [],
    bookingCalendarId: undefined,
    timezone: undefined,
    calendars: [],
    nylasConnections: [],

    setDailyOpenHoursSettings(initial) {
      set((state) => {
        state.dailyOpenHoursSettings = initial ?? defaultSettings;
      });
    },

    toggleDay(day) {
      set((state) => {
        state.dailyOpenHoursSettings[day].enabled =
          !state.dailyOpenHoursSettings[day].enabled;
      });
    },

    addTimeRange(day: DayOfWeek) {
      set((state) => {
        const daySettings = state.dailyOpenHoursSettings[day];
        const existingRanges = daySettings.timeRanges;

        // Determine the next slot—use the default if none exist, otherwise add an hour
        const nextSlot =
          existingRanges.length === 0
            ? defaultSettings[day].timeRanges[0]
            : getNextTimeRange(existingRanges.at(-1)!.endTime, {
                lengthMinutes: 60,
              });

        // If there's no valid next slot, return here
        if (!nextSlot) {
          return;
        }

        // Abort if the new slot overlaps any existing range
        const intervals = existingRanges.map(({ startTime, endTime }) => ({
          startTime,
          endTime,
        }));
        if (!validateTimeRangeOverlap([...intervals, nextSlot])) {
          return;
        }

        existingRanges.push({ id: localId(), ...nextSlot });
      });
    },

    removeTimeRange(day, rangeId) {
      set((state) => {
        state.dailyOpenHoursSettings[day].timeRanges =
          state.dailyOpenHoursSettings[day].timeRanges.filter(
            (range: TimeRange) => range.id !== rangeId,
          );
      });
    },

    handleTimeChange(day, rangeId, field, value) {
      set((state) => {
        const range = state.dailyOpenHoursSettings[day].timeRanges.find(
          (range: TimeRange) => range.id === rangeId,
        );
        if (range) range[field] = value;
      });
    },

    setCalendarIds(ids) {
      set((state) => {
        state.calendarIds = ids;
      });
    },

    setBookingCalendarId(id) {
      set((state) => {
        state.bookingCalendarId = id;
      });
    },

    setTimezone(timezone) {
      set((state) => {
        state.timezone = timezone;
      });
    },

    setCalendars(calendars) {
      set((state) => {
        state.calendars = calendars;
      });
    },

    setNylasConnections(connections) {
      set((state) => {
        state.nylasConnections = connections;
      });
    },

    setStylistId(stylistId) {
      set((state) => {
        state.stylistId = stylistId;
      });
    },

    disconnectCalendar() {
      set((state) => {
        state.calendarIds = [];
        state.bookingCalendarId = undefined;
        state.calendars = [];
        state.nylasConnections = [];
      });
    },
  })),
);

export default useAvailabilityStore;
